import "../businessAnalysis/businessAnalysis.css";
import "@src/components/compliance/compliance.css";
import "@src/components/surveys/surveys.css";
import { useEffect, useState } from "react";
import { Button, Modal, Select, Table, Tabs, Tag } from "antd";
import { useMutation, useQuery } from "@apollo/client";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useTenantFeatureFlags } from "@src/hooks/useTenantFeatureFlags";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { getMySurveysQuery, getOurSideSurveysQuery, getSurveyOverviewQuery, getSurveySchedulesQuery, getSurveysQuery } from "@src/util/query/survey.query";
import { stopSurveyScheduleMutation } from "@src/util/mutation/survey.mutation";
import { SendSurveyModal } from "@src/components/surveys/sendSurvey.modal";
import { SurveyResultsDrawer } from "@src/components/surveys/surveyResults.drawer";
import { MySurveyModal } from "@src/components/surveys/mySurvey.modal";
import { OurSideModal } from "@src/components/surveys/ourSide.modal";
import { describeRepeat, formatDate } from "@src/components/appraisals/appraisalHelpers";
import { PageNotFound } from "@src/pages/unAuthroizedAccess/pageNotFound.page";
import { CaretDownFilled } from "@ant-design/icons";

const STATUS_FILTERS = [{ value: "open", label: "Open" }, { value: "closed", label: "Closed" }, { value: "", label: "Everything" }];
const AUDIENCE_FILTERS = [{ value: "", label: "Everyone" }, { value: "employees", label: "Employees" }, { value: "customers", label: "Customers" }, { value: "suppliers", label: "Suppliers" }];

const Progress = ({ row }) => {
    const pct = row.invited ? Math.round((row.responded / row.invited) * 100) : 0;
    return (
        <span className="cmp-progress">
            <span className="cmp-progress-bar"><span className={`cmp-progress-fill${pct === 100 ? " done" : ""}`} style={{ width: `${pct}%` }} /></span>
            <span className="cmp-progress-text">{row.responded}/{row.invited}</span>
        </span>
    );
};

/**
 * Surveys: every survey sent (with results) and what's scheduled - for roles with Surveys
 * access - "My surveys" for anyone who has been sent one to answer, and "Waiting on us" for
 * anyone chosen to answer our side of two-way surveys.
 */
export const SurveysPage = () => {
    const dispatch = useDispatch();
    const { surveysEnabled, loading: flagsLoading } = useTenantFeatureFlags();
    const { authenticatedUserDetail } = useSelector((state) => state.userAuthReducer);
    const permission = authenticatedUserDetail?.permission?.Surveys;
    const isManager = permission?.access !== false;
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState(isManager ? "all" : "mine");
    const [status, setStatus] = useState("open");
    const [audienceType, setAudienceType] = useState("");
    const [sendOpen, setSendOpen] = useState(false);
    const [openId, setOpenId] = useState(searchParams.get("survey"));
    const [answering, setAnswering] = useState(null);
    const [answeringOurs, setAnsweringOurs] = useState(null);

    const { data, loading, refetch } = useQuery(getSurveysQuery, { variables: { filter: { status: status || undefined, audienceType: audienceType || undefined } }, skip: !surveysEnabled || !isManager, fetchPolicy: "network-only" });
    const { data: overviewData, refetch: refetchOverview } = useQuery(getSurveyOverviewQuery, { skip: !surveysEnabled || !isManager, fetchPolicy: "network-only" });
    const { data: schedulesData, refetch: refetchSchedules } = useQuery(getSurveySchedulesQuery, { skip: !surveysEnabled || !isManager, fetchPolicy: "network-only" });
    const { data: mineData, refetch: refetchMine } = useQuery(getMySurveysQuery, { skip: !surveysEnabled, fetchPolicy: "network-only" });
    const { data: oursData, refetch: refetchOurs } = useQuery(getOurSideSurveysQuery, { skip: !surveysEnabled, fetchPolicy: "network-only" });
    const [stopSchedule] = useMutation(stopSurveyScheduleMutation);
    const surveys = data?.getSurveys?.response || [];
    const overview = overviewData?.getSurveyOverview?.response;
    const schedules = schedulesData?.getSurveySchedules?.response || [];
    const mine = mineData?.getMySurveys?.response || [];
    const toAnswer = mine.filter((m) => m.state === "open").length;
    const ours = oursData?.getOurSideSurveys?.response || [];
    const oursToDo = ours.filter((o) => o.state === "open").length;

    // deep links: ?survey=<id> opens results; ?answer=<surveyId> (from a notification) opens my
    // survey; ?ours=<surveyId> shows the two-way reviews waiting on me
    useEffect(() => {
        const answerFor = searchParams.get("answer");
        if (answerFor && mine.length) {
            const match = mine.find((m) => m.surveyId === answerFor) || mine.find((m) => m.state === "open");
            if (match) { setTab("mine"); setAnswering(match.responseId); }
        }
        if (searchParams.get("ours") && ours.length) setTab("ours");
    }, [searchParams, mine.length, ours.length]);

    const refresh = () => { if (isManager) { refetch(); refetchOverview(); refetchSchedules(); } refetchMine(); refetchOurs(); };
    const openResults = (id) => { setOpenId(id); setSearchParams({ survey: id }, { replace: true }); };
    const closeResults = () => { setOpenId(null); setSearchParams({}, { replace: true }); };

    const stop = (row) => Modal.confirm({
        title: row.everyMonths ? `Stop repeating ${row.title}?` : `Cancel the scheduled ${row.title}?`,
        content: row.everyMonths ? "It won't be sent again. Rounds already sent stay open until they close." : `It won't go out on ${formatDate(row.nextOpenOn)}.`,
        okText: row.everyMonths ? "Stop repeating" : "Cancel it",
        okButtonProps: { danger: true },
        cancelText: "Keep it",
        onOk: async () => {
            try {
                await stopSchedule({ variables: { id: row._id } });
                dispatch(setNotification({ error: false, notificationState: true, message: row.everyMonths ? "It won't be sent again" : "Scheduled survey cancelled" }));
                refresh();
            } catch (err) { dispatch(setNotification({ error: true, notificationState: true, message: err.message })); }
        },
    });

    if (!flagsLoading && !surveysEnabled) return <PageNotFound />;

    const tabs = [
        isManager && { key: "all", label: "Surveys" },
        isManager && { key: "scheduled", label: `Scheduled (${schedules.length})` },
        ours.length > 0 && { key: "ours", label: `Waiting on us${oursToDo ? ` (${oursToDo})` : ""}` },
        (mine.length > 0 || !isManager) && { key: "mine", label: `My surveys${toAnswer ? ` (${toAnswer})` : ""}` },
    ].filter(Boolean);
    const activeTab = tabs.some((t) => t.key === tab) ? tab : tabs[0].key;

    return (
        <div style={{ padding: "25px 48px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px", marginBottom: "5px" }}>
                <div className="grid-title">
                    Surveys
                    <span className="grid-subtitle">{isManager && overview ? ` - ${overview.open} open` : ` - ${toAnswer + oursToDo} to answer`}</span>
                </div>
                {isManager && overview && (
                    <div className="ba-stat-strip">
                        <div className="ba-stat"><span className="ba-stat-num">{overview.open}</span><span className="ba-stat-lbl">Open</span></div>
                        <div className="ba-stat"><span className="ba-stat-num">{overview.awaiting}</span><span className="ba-stat-lbl">Awaiting answers</span></div>
                        <div className="ba-stat"><span className="ba-stat-num">{overview.responseRate != null ? `${overview.responseRate}%` : "-"}</span><span className="ba-stat-lbl">Response rate</span></div>
                        <div className="ba-stat"><span className="ba-stat-num">{overview.scheduled}</span><span className="ba-stat-lbl">Scheduled</span></div>
                    </div>
                )}
            </div>

            <div className="setting-body-inner grid-tabs" style={{ padding: "10px 0px" }}>
                <Tabs activeKey={activeTab} onChange={setTab} items={tabs} />
            </div>

            {activeTab === "all" ? (
                <>
                    <div className="cmp-toolbar" style={{ marginTop: -16 }}>
                        <div style={{ display: "flex", gap: 10 }}>
                            <Select className="custom-select" suffixIcon={<CaretDownFilled style={{marginBottom:'15px'}}/>} style={{ width: 300, marginTop:'-3px' }} value={status} onChange={setStatus} options={STATUS_FILTERS} />
                            <Select className="custom-select" suffixIcon={<CaretDownFilled style={{marginBottom:'15px'}}/>} style={{ width: 300, marginTop:'-3px' }} value={audienceType} onChange={setAudienceType} options={AUDIENCE_FILTERS} />
                        </div>
                        {permission?.create !== false && <Button className="grid-filed-btn" onClick={() => setSendOpen(true)}>Send survey</Button>}
                    </div>
                    <Table
                        className="curvedTable"
                        loading={loading}
                        columns={[
                            { title: "Survey", dataIndex: "title", render: (v, row) => <span><button className="cmp-link" onClick={() => openResults(row._id)}>{v}</button>{row.anonymous && <Tag color="purple" style={{ marginLeft: 6 }}>Anonymous</Tag>}{row.twoWay && <Tag color="purple" style={{ marginLeft: 6 }}>Two-way</Tag>}{row.repeatsEveryMonths && <Tag style={{ marginLeft: 6 }}>↻ {describeRepeat(row.repeatsEveryMonths)}</Tag>}</span> },
                            { title: "Sent to", dataIndex: "audience" },
                            // who sent it (a repeat: who set the repeat up)
                            { title: "Sent", dataIndex: "createdAt", render: (v, row) => <div className="cmp-person"><span>{formatDate(v)}</span><span className="cmp-muted">{row.createdByName || "--"}</span></div> },
                            { title: "Open", dataIndex: "opensOn", render: (v, row) => `${formatDate(v)} - ${formatDate(row.closesOn)}` },
                            { title: "Status", dataIndex: "status", render: (v) => <Tag color={v === "open" ? "blue" : "default"}>{v === "open" ? "Open" : "Closed"}</Tag> },
                            { title: "Answered", dataIndex: "responded", render: (_, row) => <span><Progress row={row} />{row.twoWay && <span className="cmp-muted" style={{ display: "block", fontSize: 12 }}>Our side {row.ourDone}/{row.invited}</span>}</span> },
                            { title: "", dataIndex: "open", align: "right", render: (_, row) => <button className="sm-btn" onClick={() => openResults(row._id)}>Results</button> },
                        ]}
                        dataSource={surveys.map((row) => ({ ...row, key: row._id }))}
                        pagination={{ pageSize: 25, hideOnSinglePage: true }}
                        locale={{ emptyText: status === "open" ? "No open surveys. Send one to employees, customers or suppliers." : "Nothing matches." }}
                    />
                </>
            ) : activeTab === "scheduled" ? (
                <>
                    <div className="cmp-toolbar" style={{ marginTop: -16 }}>
                        <span className="cmp-hint">Surveys set up to go out later, and ones that repeat. Each round goes to whoever is in the group at the time.</span>
                        {permission?.create !== false && <Button className="grid-filed-btn" onClick={() => setSendOpen(true)}>Send survey</Button>}
                    </div>
                    <Table
                        className="curvedTable"
                        columns={[
                            { title: "Survey", dataIndex: "title", render: (v, row) => <span className="cmp-person"><span className="cmp-person-name">{v}{row.anonymous && <Tag color="purple" style={{ marginLeft: 6 }}>Anonymous</Tag>}{row.twoWay && <Tag color="purple" style={{ marginLeft: 6 }}>Two-way</Tag>}</span><span className="cmp-muted">{row.templateName}{row.templateArchived && <Tag color="red" style={{ marginLeft: 6 }}>Template archived - waiting</Tag>}</span></span> },
                            { title: "Sent to", dataIndex: "audience" },
                            { title: "How often", dataIndex: "everyMonths", render: (v) => describeRepeat(v) },
                            { title: "Set up", dataIndex: "createdAt", render: (v, row) => <div className="cmp-person"><span>{formatDate(v)}</span><span className="cmp-muted">{row.createdByName || "--"}</span></div> },
                            { title: "Next goes out", dataIndex: "nextOpenOn", render: (v, row) => <span>{formatDate(v)}<span className="cmp-muted"> · open {row.openForDays} days</span></span> },
                            permission?.modify !== false && { title: "", dataIndex: "stop", align: "right", render: (_, row) => <button className="sm-btn" onClick={() => stop(row)}>{row.everyMonths ? "Stop repeating" : "Cancel"}</button> },
                        ].filter(Boolean)}
                        dataSource={schedules.map((row) => ({ ...row, key: row._id }))}
                        pagination={{ pageSize: 25, hideOnSinglePage: true }}
                        locale={{ emptyText: "Nothing scheduled. When you send a survey you can set it to go out later or to repeat." }}
                    />
                </>
            ) : activeTab === "ours" ? (
                <>
                    <div className="cmp-toolbar" style={{ marginTop: -16 }}>
                        <span className="cmp-hint">Two-way surveys you've been chosen to answer our side of - one for each customer or supplier. They never see our answers.</span>
                    </div>
                    <Table
                        className="curvedTable"
                        columns={[
                            { title: "Survey", dataIndex: "title" },
                            { title: "About", dataIndex: "about", render: (v, row) => <span className="cmp-person"><span className="cmp-person-name">{v}</span><span className="cmp-muted">{row.aboutType === "supplier" ? "Supplier" : "Customer"}</span></span> },
                            { title: "Closes", dataIndex: "closesOn", render: (v) => formatDate(v) },
                            { title: "Their answers", dataIndex: "theirStatus", render: (v) => (v === "submitted" ? <Tag color="green">In</Tag> : <Tag>Not yet</Tag>) },
                            { title: "Our side", dataIndex: "state", render: (v) => <Tag color={v === "submitted" ? "green" : v === "open" ? "gold" : "default"}>{v === "submitted" ? "Done" : v === "open" ? "To answer" : "Closed"}</Tag> },
                            { title: "", dataIndex: "answer", align: "right", render: (_, row) => row.state === "open" && <button className="drawer-filled-btn" onClick={() => setAnsweringOurs(row.responseId)}>Answer our side</button> },
                        ]}
                        dataSource={ours.map((row) => ({ ...row, key: row.responseId }))}
                        pagination={{ pageSize: 25, hideOnSinglePage: true }}
                    />
                </>
            ) : (
                <Table
                    className="curvedTable"
                    columns={[
                        { title: "Survey", dataIndex: "title", render: (v, row) => <span>{v}{row.anonymous && <Tag color="purple" style={{ marginLeft: 6 }}>Anonymous</Tag>}</span> },
                        { title: "Closes", dataIndex: "closesOn", render: (v) => formatDate(v) },
                        { title: "Status", dataIndex: "state", render: (v) => <Tag color={v === "submitted" ? "green" : v === "open" ? "gold" : "default"}>{v === "submitted" ? "Answered" : v === "open" ? "To answer" : "Closed"}</Tag> },
                        { title: "", dataIndex: "answer", align: "right", render: (_, row) => row.state === "open" && <button className="drawer-filled-btn" onClick={() => setAnswering(row.responseId)}>Answer</button> },
                    ]}
                    dataSource={mine.map((row) => ({ ...row, key: row.responseId }))}
                    pagination={{ pageSize: 25, hideOnSinglePage: true }}
                    locale={{ emptyText: "No surveys for you right now." }}
                />
            )}

            <SendSurveyModal visible={sendOpen} onClose={() => setSendOpen(false)} onSent={(r) => { refresh(); if (r?._id) openResults(r._id); }} />
            <SurveyResultsDrawer visible={!!openId} surveyId={openId} onClose={closeResults} onChanged={refresh} />
            <MySurveyModal responseId={answering} onClose={() => { setAnswering(null); if (searchParams.get("answer")) setSearchParams({}, { replace: true }); }} onAnswered={refresh} />
            <OurSideModal responseId={answeringOurs} onClose={() => { setAnsweringOurs(null); if (searchParams.get("ours")) setSearchParams({}, { replace: true }); }} onAnswered={refresh} />
        </div>
    );
};
